import { useMemo, useState } from "react";
import { Button } from "../components/Button";
import { Card } from "../components/Card";
import { Chip } from "../components/Chip";
import { KpiCard } from "../components/KpiCard";
import { api } from "../lib/api";
import { useFetch } from "../lib/useFetch";
import type { SubscriptionDTO, UserRole } from "@shared/api";

const ROLES = ["All", "Admin", "Solutions", "Developer", "Compliance", "Viewer"] as const;
type RoleFilter = (typeof ROLES)[number];

interface Props {
  search: string;
  onOpenSubscription: (id: string) => void;
}

export function Users({ search, onOpenSubscription }: Props) {
  const [role, setRole] = useState<RoleFilter>("All");
  const usersQ = useFetch(() => api.users.list(), []);
  const subsQ = useFetch(() => api.subscriptions.list(), []);

  const list = useMemo(() => {
    const users = usersQ.data ?? [];
    return users.filter(
      (u) =>
        (role === "All" || u.role === role) &&
        (!search ||
          u.name.toLowerCase().includes(search.toLowerCase()) ||
          u.email.toLowerCase().includes(search.toLowerCase())),
    );
  }, [usersQ.data, role, search]);

  const totalSeats = (subsQ.data ?? []).reduce((a, s) => a + s.seatsTotal, 0);
  const usedSeats = (subsQ.data ?? []).reduce((a, s) => a + s.seatsUsed, 0);
  const subById = useMemo(() => {
    const m = new Map<string, SubscriptionDTO>();
    for (const s of subsQ.data ?? []) m.set(s.id, s);
    return m;
  }, [subsQ.data]);

  return (
    <div className="bbc-screen">
      <div className="bbc-screen-head">
        <div>
          <h1 className="bbc-h1">Users & Seats</h1>
          <p className="bbc-screen-sub">
            Users are licensed per-seat against specific subscriptions. A user
            occupies one seat per subscription they're assigned to.
          </p>
        </div>
        <div className="bbc-screen-actions">
          <Button variant="secondary">Import CSV</Button>
          <Button variant="primary">+ Invite user</Button>
        </div>
      </div>

      <div className="bbc-kpi-grid bbc-kpi-grid-3">
        <KpiCard
          label="Total users"
          value={usersQ.data?.length ?? 0}
          hint="across all subscriptions"
        />
        <KpiCard
          label="Seats consumed"
          value={usedSeats}
          unit={`/ ${totalSeats}`}
          hint={
            totalSeats > 0
              ? `${Math.round((usedSeats / totalSeats) * 100)}% utilization`
              : "no seats yet"
          }
        />
        <KpiCard
          label="Admins"
          value={
            (usersQ.data ?? []).filter((u) => u.role === "Admin").length
          }
          hint="full configure access"
        />
      </div>

      <div className="bbc-toolbar">
        <div className="bbc-segmented">
          {ROLES.map((r) => {
            const count =
              r === "All"
                ? usersQ.data?.length ?? 0
                : (usersQ.data ?? []).filter((u) => u.role === (r as UserRole))
                    .length;
            return (
              <button
                key={r}
                type="button"
                className={role === r ? "is-on" : ""}
                onClick={() => setRole(r)}
              >
                {r}
                {r !== "All" && ` · ${count}`}
              </button>
            );
          })}
        </div>
      </div>

      <Card padded={false}>
        {usersQ.loading ? (
          <div className="bbc-screen-loading">Loading users…</div>
        ) : usersQ.error ? (
          <div className="bbc-error">{usersQ.error.message}</div>
        ) : (
          <table className="bbc-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Subscriptions assigned</th>
                <th className="num">Seats</th>
                <th>Last active</th>
                <th aria-hidden="true" />
              </tr>
            </thead>
            <tbody>
              {list.map((u) => (
                <tr key={u.id}>
                  <td>
                    <div className="bbc-cell-user">
                      <span className="bbc-avatar bbc-avatar-sm">
                        {u.initials}
                      </span>
                      <div>
                        <div>{u.name}</div>
                        <div className="bbc-cell-sub">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <Chip tone={u.role === "Admin" ? "accent" : "muted"}>
                      {u.role}
                    </Chip>
                  </td>
                  <td>
                    <div className="bbc-user-subs">
                      {u.seats.map((sid) => {
                        const s = subById.get(sid);
                        if (!s) return null;
                        return (
                          <button
                            key={sid}
                            type="button"
                            className="bbc-user-sub-chip mono"
                            onClick={() => onOpenSubscription(sid)}
                          >
                            {s.code}
                          </button>
                        );
                      })}
                    </div>
                  </td>
                  <td className="num mono">{u.seats.length}</td>
                  <td className="bbc-cell-sub">{u.lastActive}</td>
                  <td>
                    <button type="button" className="bbc-text-btn-sm">
                      Manage
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
